import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import * as yup from "yup";
import { actionShowSpinner } from "../../../../../../redux-flow/action/Wrapper/Spinner";
import { getAll } from "../../../../../../services/committePackages.service";
import { list } from "../../../../../../services/functionalities.service";
import {
  createGroup,
  getGroupInfo,
  toggleActive,
  updateGroup,
  verifyGroupMembers,
} from "../../../../../../services/groups.service";
import DialogCustom from "../../../../../Common/DialogCustom";
import ToastMessage from "../../../../../Common/ToastMessage";
import { IFormattedFunctionality } from "../../../Modals/CreateGroup";
import AcessModelCard, {
  IAcessModelValues,
} from "../../../components/Boxes/AccessModelCard";
import FuncionalityAcessCard from "../../../components/Boxes/FuncionalityAcessCard";
import GroupModalitiesCard from "../../../components/Boxes/GroupModalitiesCard";
import ParticipantsCard from "../../../components/Boxes/ParticipantsCard";
import Header from "../../../components/Header";
import {
  ActionButtonsContainer,
  CardColumn,
  Container,
  FormContainer,
  GroupInfoInputsContainer,
  GroupInfoPaper,
  SectionTitle,
  Title,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    border: `2px solid ${theme.palette.primary.main}`,
    "&:hover": {
      borderColor: "#526bc5",
      backgroundColor: "#526bc5",
      color: theme.palette.primary.contrastText,
    },
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: "#666666",
    textTransform: "capitalize",
  },
}));

const schema = yup.object().shape({
  name: yup.string().required(),
  committeePackageName: yup.string().required(),
  committeePackageId: yup.string().required(),
});

interface IGroupData {
  name: string;
  committeePackageId: string;
  committeePackageName: string;
}

const GroupConfiguration: React.FC = ({ actionShowSpinner }: any) => {
  const location = useLocation<{ id?: string }>();
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const effectHasAlreadyRunned = useRef(false);
  const { register, handleSubmit, watch, setValue } = useForm<IGroupData>({
    resolver: yupResolver(schema),
  });

  const [selectedPackageIsAdmin, setSelectedPackageIsAdmin] = useState(false);
  const [groupId, setGroupId] = useState<string | undefined>();
  const [toastMessage, setToastMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [acessModelValues, setAcessModelValues] = useState<IAcessModelValues>({
    allResources: true,
    onlyRelatedToDirectory: false,
    onlyRelatedToOwner: false,
    onlyRelatedToModalities: false,
  });
  const [selectedModalities, setSelectedModalities] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedFuncionalities, setSelectedFuncionalities] = useState<
    string[]
  >([]);
  const [participants, setParticipants] = useState<
    { id: string; name: string }[]
  >([]);
  const [functionalities, setFunctionalities] = useState<
    IFormattedFunctionality[]
  >([]);
  const [committeePackages, setCommitteePackages] = useState<
    { id: string; name: string; admin: boolean }[]
  >([]);
  const [groupActive, setGroupActive] = useState(false);
  const [disableGroupDialogOpened, setDisableGroupDialogOpened] =
    useState(false);
  const [
    groupParticipantsArePartOfPackage,
    setGroupParticipantsArePartOfPackage,
  ] = useState<boolean>(false);

  const fetchCommitteePackages = useCallback(async () => {
    actionShowSpinner(true);

    try {
      const { committeePackages } = await getAll();

      setCommitteePackages(committeePackages);
    } catch (error: any) {
      const [errorMessage] = error?.response?.data?.errors || [
        t("softwareManagement.modules.business.errorFetchCommitteePackages"),
      ];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });
    }

    actionShowSpinner(false);
  }, []);

  const fetchFunctionalities = useCallback(async () => {
    actionShowSpinner(true);

    try {
      const { functionalities } = await list(selectedPackageIsAdmin);

      const formattedFunctionalities = functionalities.map((functionality) => ({
        ...functionality,
        hidden: false,
      }));

      setFunctionalities(formattedFunctionalities);
    } catch (error: any) {
      const [errorMessage] = error?.response?.data?.errors || [
        t("softwareManagement.modules.groups.errorFetchFunctionalities"),
      ];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });
    }

    actionShowSpinner(false);
  }, [selectedPackageIsAdmin]);

  const fetchGroup = useCallback(async (groupId: string) => {
    try {
      const group = await getGroupInfo(groupId);

      setValue("name", group.name);
      setValue("committeePackageName", group.committeePackageName);
      setValue("committeePackageId", group.committeePackageId);
      setSelectedModalities(group.modalities);
      setAcessModelValues({
        allResources: group.accessModelAllContent,
        onlyRelatedToDirectory: group.accessModelDirectoryBased,
        onlyRelatedToOwner: group.accessModelResponsibleBased,
        onlyRelatedToModalities: group.accessModelModalityBased,
      });
      setSelectedPackageIsAdmin(
        group.groupAdminLevel === 1 || group.groupAdminLevel === 2
      );
      setParticipants(group.participants);
      setSelectedFuncionalities(
        group.functionalities.map(
          (functionality: { id: string }) => functionality.id
        )
      );
      setGroupActive(group.active);
    } catch (error: any) {
      const [errorMessage] = error?.response?.data?.errors || [
        t("softwareManagement.modules.groups.errorFetchGroup"),
      ];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");

    if (id) {
      setGroupId(id || undefined);
    }

    fetchFunctionalities();

    if (effectHasAlreadyRunned.current) {
      return;
    }
    effectHasAlreadyRunned.current = true;

    fetchCommitteePackages();

    effectHasAlreadyRunned.current = true;
  }, [effectHasAlreadyRunned, fetchFunctionalities, location]);

  useEffect(() => {
    if (groupId) {
      fetchGroup(groupId);
    }
  }, [groupId]);

  const handleCreateGroup = useCallback(
    async (data: IGroupData) => {
      try {
        const modalitiesIds = selectedModalities.map((modality) => modality.id);

        const createdGroupId = await createGroup({
          name: data.name,
          committeePackageId: data.committeePackageId,
          functionalitiesIds: selectedFuncionalities,
          groupAdminLevel: selectedPackageIsAdmin ? 2 : 3,
          participantsIds: participants.map((participant) => participant.id),
          modalitiesIds,
          accessModelAllContent: acessModelValues.allResources,
          accessModelModalityBased: acessModelValues.onlyRelatedToModalities,
          accessModelDirectoryBased: acessModelValues.onlyRelatedToDirectory,
          accessModelResponsibleBased: acessModelValues.onlyRelatedToOwner,
        });

        setGroupId(createdGroupId);

        setToastMessage({
          open: true,
          content: t("softwareManagement.modules.groups.successCreation"),
          type: "success",
          autoHideDuration: 4000,
        });
      } catch (err: any) {
        const [errorMessage] = err?.response?.data?.errors || [
          t("softwareManagement.modules.groups.errorCreation"),
        ];

        setToastMessage({
          open: true,
          content:
            errorMessage ||
            t("softwareManagement.modules.groups.errorCreation"),
          type: "error",
          autoHideDuration: 4000,
        });
      }
    },
    [
      selectedModalities,
      acessModelValues,
      selectedFuncionalities,
      selectedPackageIsAdmin,
      participants,
    ]
  );

  const handleUpdateGroup = async (data: IGroupData) => {
    try {
      const modalitiesIds = selectedModalities.map((modality) => modality.id);

      await updateGroup({
        id: groupId,
        name: data.name,
        committeePackageId: data.committeePackageId,
        modalitiesIds,
        accessModelModalityBased: acessModelValues.onlyRelatedToModalities,
        functionalitiesIds: selectedFuncionalities,
        participantsIds: participants.map((participant) => participant.id),
        accessModelAllContent: acessModelValues.allResources,
        accessModelDirectoryBased: acessModelValues.onlyRelatedToDirectory,
        accessModelResponsibleBased: acessModelValues.onlyRelatedToOwner,
      });

      setToastMessage({
        open: true,
        content: t("softwareManagement.modules.groups.successUpdate"),
        type: "success",
        autoHideDuration: 4000,
      });
    } catch (err: any) {
      const [errorMessage] = err?.response?.data?.errors || [
        t("softwareManagement.modules.groups.errorUpdate"),
      ];

      setToastMessage({
        open: true,
        content:
          errorMessage || t("softwareManagement.modules.groups.errorUpdate"),
        type: "error",
        autoHideDuration: 4000,
      });
    }
  };

  const handleTriggerSubmition = async (data: IGroupData) => {
    actionShowSpinner(true);
    if (!groupId) {
      await handleCreateGroup(data);
    } else {
      await handleUpdateGroup(data);
    }
    setGroupParticipantsArePartOfPackage(false);
    actionShowSpinner(false);
  };

  const onSubmit = useCallback(
    async (data: IGroupData, e) => {
      e.stopPropagation();
      e.preventDefault();

      const areMembersPartOfOthePackageGroup = await verifyGroupMembers({
        committeePackageId: data?.committeePackageId,
        groupId: groupId,
        participantsIds: participants.map((participant) => participant.id),
      });

      if (areMembersPartOfOthePackageGroup) {
        setGroupParticipantsArePartOfPackage(true);

        return;
      }

      handleTriggerSubmition(data);
    },
    [handleCreateGroup, handleUpdateGroup, groupId, participants]
  );

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
    return;
  };

  const handleClickOpen = (_event: any) => {
    setDisableGroupDialogOpened(true);
  };

  const handleToggleActive = useCallback(async () => {
    if (!groupId) return;

    actionShowSpinner(true);

    try {
      await toggleActive(groupId, !groupActive);

      setGroupActive(!groupActive);
    } catch (err: any) {
      const [errorMessage] = err?.response?.data?.errors || [
        t("softwareManagement.modules.business.errorToggleActive"),
      ];

      setToastMessage({
        autoHideDuration: 4000,
        content:
          errorMessage ||
          t("softwareManagement.modules.business.errorToggleActive"),
        open: true,
        type: "error",
      });
    }

    setDisableGroupDialogOpened(false);
    actionShowSpinner(false);
  }, [groupActive, groupId]);

  return (
    <Container onKeyPress={handleKeyPress} onSubmit={handleSubmit(onSubmit)}>
      <DialogCustom
        open={groupParticipantsArePartOfPackage}
        handleClose={() => {
          setGroupParticipantsArePartOfPackage(false);
        }}
        handleChange={handleSubmit(handleTriggerSubmition)}
        title={t(`forms.dialog.alert`)}
        body={t(
          "softwareManagement.modules.groups.alerts.userIsPartOfAnotherPackageGroup"
        )}
        confirmText={t(`forms.dialog.yes`)}
        cancelText={t(`forms.dialog.no`)}
        alert={true}
      />
      <Header title={t("softwareManagement.modules.groups.settings")}></Header>

      <FormContainer>
        <CardColumn>
          <GroupInfoPaper>
            <SectionTitle>
              <Title>
                {t("softwareManagement.modules.committeePackage.infomations")}
              </Title>

              {groupId && (
                <FormControlLabel
                  control={
                    <Tooltip
                      title={groupActive ? t("deactivate") : t("activate")}
                    >
                      <Switch
                        checked={groupActive}
                        onChange={handleClickOpen}
                        color="primary"
                      />
                    </Tooltip>
                  }
                  label={groupActive ? t("active") : t("inactive")}
                />
              )}
            </SectionTitle>

            <GroupInfoInputsContainer container spacing={2}>
              <Grid item lg={6} xs={12}>
                <TextField
                  autoComplete="nope"
                  id="name"
                  label={t("softwareManagement.modules.groups.groupName")}
                  fullWidth
                  required
                  error={!watch("name")}
                  {...register("name")}
                  InputLabelProps={{ shrink: watch("name") ? true : false }}
                />
              </Grid>

              <Grid item lg={6} xs={12}>
                <Autocomplete
                  options={committeePackages}
                  getOptionLabel={(option) => option.name || ""}
                  onChange={(event, newValue) => {
                    setValue("committeePackageId", newValue?.id || "");
                    setSelectedPackageIsAdmin(newValue?.admin || false);
                  }}
                  value={
                    watch("committeePackageId")
                      ? committeePackages.find(
                          (committeePackage) =>
                            committeePackage.id === watch("committeePackageId")
                        )
                      : null
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t(
                        "softwareManagement.modules.committeePackage.title"
                      )}
                      required
                      error={!watch("committeePackageName")}
                      value={watch("committeePackageName")}
                      {...register("committeePackageName")}
                    />
                  )}
                />
              </Grid>
            </GroupInfoInputsContainer>
          </GroupInfoPaper>

          <FuncionalityAcessCard
            selectedPackageIsAdmin={selectedPackageIsAdmin}
            selectedFuncionalities={selectedFuncionalities}
            setSelectedFuncionalities={setSelectedFuncionalities}
            functionalities={functionalities}
            setFunctionalities={setFunctionalities}
          />
        </CardColumn>

        <CardColumn>
          <AcessModelCard
            acessModelValues={acessModelValues}
            setAcessModelValues={setAcessModelValues}
          />

          {acessModelValues.onlyRelatedToModalities && (
            <GroupModalitiesCard
              selectedModalities={selectedModalities}
              setSelectedModalities={setSelectedModalities}
            />
          )}
          <ParticipantsCard
            participants={participants}
            setParticipants={setParticipants}
          />
        </CardColumn>
      </FormContainer>

      <ActionButtonsContainer>
        <Button
          variant="outlined"
          className={classes.buttonOutlined}
          onClick={() => history.push("/system-management/groups")}
          type="button"
        >
          Cancelar
        </Button>
        <Button variant="contained" className={classes.button} type="submit">
          Salvar
        </Button>
      </ActionButtonsContainer>
      <DialogCustom
        open={disableGroupDialogOpened}
        handleClose={() => {
          setDisableGroupDialogOpened(false);
        }}
        handleChange={() => handleToggleActive()}
        title={t(`forms.dialog.alert`)}
        body={
          groupActive
            ? t("softwareManagement.modules.groups.textInactivateSlot")
            : t("softwareManagement.modules.groups.textActivateSlot")
        }
        confirmText={t(`forms.dialog.yes`)}
        cancelText={t(`forms.dialog.no`)}
        alert={true}
      />
      <ToastMessage
        message={toastMessage.content}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
    </Container>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  actionShowSpinner: (request: any) => dispatch(actionShowSpinner(request)),
});

const GroupConfigurationConnection = connect(
  null,
  mapDispatchToProps
)(GroupConfiguration);

export default GroupConfigurationConnection;
